import { supabase } from "@/integrations/supabase/client";

export interface Material {
  id: string;
  codigo?: string;
  nome: string;
  categoria: string;
  unidade: string;
  status: "Ativo" | "Inativo";
  sinonimos: string[];
  descricao?: string;
  createdAt: string;
  updatedAt: string;
}

// Catálogo de referência (estático, não persistido). Os materiais do usuário
// ficam no Supabase (tabela `materiais`) e são hidratados em memória no login.
const DEFAULT_CATALOG_MATERIALS: Material[] = [
  { id: "cat-1", codigo: "MAT-001", nome: "Cimento CP-II", categoria: "Estrutura", unidade: "saco", status: "Ativo", sinonimos: ["cimento comum"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-2", codigo: "MAT-002", nome: "Areia Média", categoria: "Agregados", unidade: "m³", status: "Ativo", sinonimos: ["areia"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-3", codigo: "MAT-003", nome: "Brita 1", categoria: "Agregados", unidade: "m³", status: "Ativo", sinonimos: ["pedra 1"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-4", codigo: "MAT-004", nome: "Ferro 10mm", categoria: "Estrutura", unidade: "kg", status: "Ativo", sinonimos: ["vergalhão", "ferro CA-50"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-5", codigo: "MAT-005", nome: "Tubo PVC 50mm", categoria: "Hidráulica", unidade: "un", status: "Ativo", sinonimos: ["cano pvc"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-6", codigo: "MAT-006", nome: "Fio 2,5mm", categoria: "Elétrica", unidade: "m", status: "Ativo", sinonimos: ["cabo elétrico"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-7", codigo: "MAT-007", nome: "Tinta Acrílica Branca", categoria: "Acabamento", unidade: "L", status: "Ativo", sinonimos: ["tinta branca"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-8", codigo: "MAT-008", nome: "Cerâmica 45x45", categoria: "Acabamento", unidade: "m²", status: "Ativo", sinonimos: ["piso cerâmico"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-9", codigo: "MAT-009", nome: "Aço CA-50 8mm", categoria: "Estrutura", unidade: "kg", status: "Ativo", sinonimos: ["ferro 8mm", "vergalhão 8mm"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: "cat-10", codigo: "MAT-010", nome: "Bloco Cerâmico 14x19x29", categoria: "Alvenaria", unidade: "un", status: "Ativo", sinonimos: ["tijolo", "bloco"], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
];

// Materiais do usuário (Supabase), hidratados em memória.
let _sessionMaterials: Material[] = [];

function rowToMaterial(row: any): Material {
  return {
    id: row.id,
    codigo: row.codigo ?? undefined,
    nome: row.nome,
    categoria: row.categoria ?? "",
    unidade: row.unidade ?? "",
    status: (row.status as Material["status"]) ?? "Ativo",
    sinonimos: row.sinonimos ?? [],
    descricao: row.descricao ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function currentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export class MaterialCache {
  // Hidrata do Supabase (chamar após login).
  static async hydrate(): Promise<void> {
    const { data, error } = await supabase.from("materiais").select("*").order("created_at", { ascending: false });
    if (error) { console.error("MaterialCache.hydrate:", error.message); return; }
    _sessionMaterials = (data ?? []).map(rowToMaterial);
  }

  static getCatalogMaterials(): Material[] {
    return DEFAULT_CATALOG_MATERIALS;
  }

  static setCatalogMaterials(_materials: Material[]): void {
    // Catálogo é referência estática; edição do catálogo não é persistida.
  }

  static getSessionMaterials(): Material[] {
    return _sessionMaterials;
  }

  static setSessionMaterials(materials: Material[]): void {
    _sessionMaterials = materials;
  }

  static addSessionMaterial(material: Omit<Material, "id" | "createdAt" | "updatedAt">): Material {
    const now = new Date().toISOString();
    const temp: Material = { ...material, id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, createdAt: now, updatedAt: now };
    _sessionMaterials = [temp, ..._sessionMaterials];
    // Write-through para o Supabase (assíncrono; reconcilia no próximo hydrate).
    (async () => {
      const userId = await currentUserId();
      if (!userId) return;
      const { data, error } = await supabase.from("materiais").insert({
        user_id: userId,
        codigo: material.codigo ?? null,
        nome: material.nome,
        categoria: material.categoria ?? null,
        unidade: material.unidade ?? null,
        status: material.status ?? "Ativo",
        sinonimos: material.sinonimos ?? [],
        descricao: material.descricao ?? null,
      }).select("*").single();
      if (error) { console.error("MaterialCache.addSessionMaterial:", error.message); return; }
      // Substitui o registro temporário pelo real (com uuid do banco).
      _sessionMaterials = _sessionMaterials.map((m) => (m.id === temp.id ? rowToMaterial(data) : m));
    })();
    return temp;
  }

  static updateSessionMaterial(updated: Material): void {
    _sessionMaterials = _sessionMaterials.map((m) =>
      m.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : m
    );
    (async () => {
      const { error } = await supabase.from("materiais").update({
        codigo: updated.codigo ?? null,
        nome: updated.nome,
        categoria: updated.categoria ?? null,
        unidade: updated.unidade ?? null,
        status: updated.status ?? "Ativo",
        sinonimos: updated.sinonimos ?? [],
        descricao: updated.descricao ?? null,
      }).eq("id", updated.id);
      if (error) console.error("MaterialCache.updateSessionMaterial:", error.message);
    })();
  }

  static deleteSessionMaterial(id: string): void {
    _sessionMaterials = _sessionMaterials.filter((m) => m.id !== id);
    (async () => {
      const { error } = await supabase.from("materiais").delete().eq("id", id);
      if (error) console.error("MaterialCache.deleteSessionMaterial:", error.message);
    })();
  }

  static importFromCatalog(materialId: string): Material | null {
    const material = DEFAULT_CATALOG_MATERIALS.find((m) => m.id === materialId);
    if (!material) return null;
    const already = _sessionMaterials.find((m) => m.nome === material.nome && m.categoria === material.categoria);
    if (already) return already;
    const { id, createdAt, updatedAt, ...rest } = material;
    return this.addSessionMaterial(rest);
  }

  static getAllAvailableMaterials(): Material[] {
    return [..._sessionMaterials, ...DEFAULT_CATALOG_MATERIALS].filter((m) => m.status === "Ativo");
  }

  static searchMaterials(searchTerm: string): Material[] {
    const all = this.getAllAvailableMaterials();
    if (!searchTerm.trim()) return all;
    const term = searchTerm.toLowerCase();
    return all.filter((material) =>
      material.nome.toLowerCase().includes(term) ||
      material.codigo?.toLowerCase().includes(term) ||
      material.categoria.toLowerCase().includes(term) ||
      material.sinonimos.some((s) => s.toLowerCase().includes(term))
    );
  }

  static getMaterialById(id: string): Material | null {
    return this.getAllAvailableMaterials().find((m) => m.id === id) || null;
  }

  static clearSession(): void {
    _sessionMaterials = [];
  }
}
